/**
 * Overview, where an account always opens (Customer 360, decision 3). The card
 * above it has said where the account stands, what needs attention and where the
 * work is; Overview holds what to act on and read next — any promise waiting for
 * an answer, the recent activity where the rail is not beside the page, the bills
 * past their due date, and what the account's own records say about how it pays.
 * Nothing here repeats a figure from the card (owner, 24 Oct 2026: the next-action
 * panel's facts live in the card's work band, its actions in the toolbar).
 */
import { Link } from 'react-router-dom';
import { Panel, ProportionBar, RowHead, Table, Td, Th } from '@timely/ui';
import { chequeBasis, daysFrom } from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import { OpenPromises } from '../../book/OpenPromises';
import { AiReportCard } from '../../ai/AiReportCard';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { useAccount } from './accountPage';
import { useTabTo } from './AccountTabs';
import { ActivityRail } from './Timeline';

export function CustomerOverview() {
    const page = useAccount();
    const { workspace } = useActiveWorkspace();
    const pays = usePays();
    const promises = page.rights.record && page.openPromises.length > 0;
    return (
        <div className="space-y-6">
            {/* Nothing to act on or read here, beside the rail: one quiet line saying where the rest is, never an empty tab. */}
            {!promises && !hasOverdueBills(page) && !pays && (
                <p className="max-xl:hidden text-secondary text-label-3 py-1">
                    Nothing more to act on here. The bills are under Bills, and the people to reach under Contacts.
                </p>
            )}
            {promises && (
                <OpenPromises
                    promises={page.openPromises}
                    workspaceId={workspace.id}
                    customerId={page.customer.id}
                    updatedAt={page.updatedAt}
                    disabled={page.busy}
                    onAnswered={page.changed}
                    onError={page.failed}
                />
            )}
            {/* The rail beside the page holds it from 1280px; narrower, it is part of the page, before the detail. */}
            <div className="xl:hidden"><ActivityRail limit={5} onContinue={page.openRecord} /></div>
            <OverdueBills />
            {pays && <HowItPays pays={pays} />}
            {page.rights.useAi && (
                <AiReportCard
                    customerId={page.customer.id}
                    customerName={page.customer.company}
                    basisNow={{
                        customerUpdatedAt: page.updatedAt,
                        latestActivityAt: page.entries[0]?.createdAt ?? null,
                        cheques: page.rights.seeCheques ? chequeBasis(page.cheques.map(c => ({ status: c.status, amount: c.amount }))) : null,
                    }}
                />
            )}
        </div>
    );
}

/** TallyPrime's bills are read, and some are past their due date. */
const hasOverdueBills = (page: ReturnType<typeof useAccount>) =>
    page.source === 'tally' && !!page.statement.statement?.billWise && page.overdue.kind === 'bills' && page.overdue.overdue.amount > 0;

/** The bills past their due date, oldest first, with how late each is: the overdue the card names, bill by bill. */
function OverdueBills() {
    const page = useAccount();
    const tabTo = useTabTo();
    const { money, date, count } = useFormatters();
    const s = page.statement.statement;
    if (!hasOverdueBills(page) || !s || page.overdue.kind !== 'bills') return null;
    const bills = s.bills.filter(b => b.standing.kind === 'overdue' || b.standing.kind === 'grace');
    const days = (b: typeof bills[number]) => (b.standing.kind === 'overdue' || b.standing.kind === 'grace' ? b.standing.days : 0);
    const allBills = tabTo('bills');
    return (
        <Panel
            headingLevel={3} title="Overdue bills"
            description={`Past their due date, oldest first. Of ${count(s.bills.length)} open ${s.bills.length === 1 ? 'bill' : 'bills'}.`}
            actions={<Link to={allBills} className="text-secondary font-semibold text-accent-text hover:underline whitespace-nowrap">All bills</Link>}
        >
            <Table caption="Overdue bills, oldest first" aria-label="Overdue bills">
                <thead>
                    <tr>
                        <Th>Bill</Th>
                        <Th hideBelow="sm">Bill date</Th>
                        <Th hideBelow="md">Due</Th>
                        <Th hideBelow="sm">Past due</Th>
                        <Th numeric>Pending</Th>
                    </tr>
                </thead>
                <tbody>
                    {bills.map(b => (
                        <tr key={b.key}>
                            <RowHead>
                                <span className="text-secondary font-semibold text-label break-words">{b.ref || 'No reference'}</span>
                                <span className="block @min-[36rem]:hidden text-caption font-normal">
                                    <span className="text-dang font-semibold">{count(days(b))} days past due</span>
                                    <span className="text-label-3">{b.dueDate ? ` · due ${date.short(b.dueDate)}` : ''}</span>
                                </span>
                            </RowHead>
                            <Td hideBelow="sm" className="text-secondary text-label-2 num whitespace-nowrap">{date.day(b.billDate)}</Td>
                            <Td hideBelow="md" className="text-secondary text-label-2 num whitespace-nowrap">{b.dueDate ? date.day(b.dueDate) : 'From the bill date'}</Td>
                            <Td hideBelow="sm" className="text-secondary text-dang font-semibold num whitespace-nowrap">
                                {count(days(b))} {days(b) === 1 ? 'day' : 'days'}
                                {b.standing.kind === 'grace' && <span className="block text-caption font-normal text-label-3">inside the grace</span>}
                            </Td>
                            <Td numeric className="text-secondary font-semibold text-label">{money.precise(b.pending)}</Td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <dl className="border-t border-separator px-5 py-3 flex items-baseline justify-between gap-4">
                <dt className="text-secondary font-semibold text-label">Overdue · {count(bills.length)} of {count(s.bills.length)} open {s.bills.length === 1 ? 'bill' : 'bills'}</dt>
                <dd className="text-secondary font-semibold num text-dang whitespace-nowrap">{money.precise(page.overdue.overdue.amount)}</dd>
            </dl>
        </Panel>
    );
}

/**
 * What the account's own records say about how it pays (01 §5, derived facts, for the owner's reading): promises
 * kept and not kept, counted from its entries; about how long after a bill it pays, from the bills that closed in the
 * last 180 days — approximate, and said to be; and the share of the credit limit in use. Each only where it exists.
 */
function usePays() {
    const page = useAccount();
    const { date } = useFormatters();
    const answered = page.entries.filter(e => e.resolvesId && page.entries.some(p => p.id === e.resolvesId && p.kind === 'promise'));
    const kept = answered.filter(e => e.kind === 'payment').length;
    const today = date.todayValue();
    const cleared = page.source === 'tally' && page.statement.statement?.billWise
        ? page.statement.statement.cleared.filter(c => daysFrom(c.clearedOn, today) <= 180 && daysFrom(c.billDate, c.clearedOn) >= 0)
        : [];
    const paysAfter = cleared.length >= 2 ? Math.round(cleared.reduce((s, c) => s + daysFrom(c.billDate, c.clearedOn), 0) / cleared.length) : null;
    const limit = page.customer.creditLimit && page.customer.creditLimit > 0 ? page.customer.creditLimit : null;
    const owed = page.customer.totalType === 'Cr' ? 0 : Math.abs(Number(page.customer.total) || 0);
    if (answered.length === 0 && paysAfter === null && limit === null) return null;
    return { answered, kept, notKept: answered.length - kept, cleared, paysAfter, limit, owed };
}

function HowItPays({ pays }: { pays: NonNullable<ReturnType<typeof usePays>> }) {
    const { money, count } = useFormatters();
    const { answered, kept, notKept, cleared, paysAfter, limit, owed } = pays;
    return (
        <Panel headingLevel={3} title="How this account pays" description="Worked out from this account’s own records: approximate, and never a score.">
            <dl className="px-5 pb-5 grid gap-x-8 gap-y-4 sm:grid-cols-3">
                {answered.length > 0 && (
                    <div>
                        <dt className="text-caption font-semibold text-label-3">Promises answered</dt>
                        <dd className="mt-1 text-secondary font-semibold text-label">{count(kept)} kept · {count(notKept)} not kept</dd>
                    </div>
                )}
                {paysAfter !== null && (
                    <div>
                        <dt className="text-caption font-semibold text-label-3">Pays</dt>
                        <dd className="mt-1 text-secondary font-semibold text-label">about {count(paysAfter)} days after a bill</dd>
                        <dd className="text-caption text-label-3">from {count(cleared.length)} bills cleared in the last 180 days; when TallyPrime showed them closed, not when the money arrived</dd>
                    </div>
                )}
                {limit !== null && (
                    <div>
                        <dt className="text-caption font-semibold text-label-3">Credit limit</dt>
                        <dd className="mt-1 text-secondary font-semibold text-label num">{money.exact(limit)} · {Math.round((owed / limit) * 100)}% used</dd>
                        <dd className="mt-2">
                            <ProportionBar share={Math.min(1, owed / limit)} tone={owed > limit ? 'dang' : 'accent'}
                                label={`${money.exact(owed)} of a ${money.exact(limit)} credit limit`} />
                        </dd>
                    </div>
                )}
            </dl>
        </Panel>
    );
}
